import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { DataTable, DataTableColumn } from '../../components/ui/data-table';
import InlineFilter from '../../components/ui/inline-filter';
import ActionDropdown from '../../components/common/action-dropdown';
import PageHeader from '../../components/common/PageHeader';
import {
  Users,
  DollarSign,
  Target,
  Gift,
  Share2,
  Download,
  Filter,
  BarChart3,
  Award,
  Megaphone,
} from 'lucide-react';
import { getStatusColor, capitalizeFirst } from '../../utils/helpers';

interface IBPartner {
  id: string;
  partnerId: string;
  name: string;
  email: string;
  phone: string;
  country: string;
  registrationDate: string;
  status: 'active' | 'pending' | 'suspended';
  tier: 'Bronze' | 'Silver' | 'Gold' | 'Platinum';
  totalReferrals: number;
  activeReferrals: number;
  totalCommission: number;
  currentMonthCommission: number;
  lastActivity: string;
  marketingMaterials: number;
  conversionRate: number;
}

interface MarketingMaterial {
  id: string;
  title: string;
  type: 'banner' | 'landing-page' | 'email-template' | 'social-media' | 'video';
  category: string;
  downloads: number;
  usage: number;
  createdAt: string;
  status: 'active' | 'draft' | 'archived';
  thumbnail?: string;
  fileUrl?: string;
}

const IBMarketing = () => {
  const [activeTab, setActiveTab] = useState<
    'overview' | 'partners' | 'materials' | 'analytics'
  >('overview');
  const [searchValue, setSearchValue] = useState('');
  const [showInlineFilters, setShowInlineFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string }>(
    {}
  );

  const [partners] = useState<IBPartner[]>([
    {
      id: '1',
      partnerId: 'IB-001234',
      name: 'John Smith',
      email: 'john.smith@ibpartner.com',
      phone: '+1 234-567-8900',
      country: 'United States',
      registrationDate: '2024-01-15',
      status: 'active',
      tier: 'Gold',
      totalReferrals: 156,
      activeReferrals: 89,
      totalCommission: 15420.5,
      currentMonthCommission: 2340.0,
      lastActivity: '2 hours ago',
      marketingMaterials: 12,
      conversionRate: 57.1,
    },
    {
      id: '2',
      partnerId: 'IB-002345',
      name: 'Sarah Johnson',
      email: 'sarah.j@ibpartner.com',
      phone: '+44 20 7123 4567',
      country: 'United Kingdom',
      registrationDate: '2024-02-20',
      status: 'active',
      tier: 'Platinum',
      totalReferrals: 289,
      activeReferrals: 201,
      totalCommission: 32150.75,
      currentMonthCommission: 4250.0,
      lastActivity: '1 day ago',
      marketingMaterials: 18,
      conversionRate: 69.6,
    },
    {
      id: '3',
      partnerId: 'IB-003456',
      name: 'Mike Chen',
      email: 'mike.chen@ibpartner.com',
      phone: '+86 138 0000 0000',
      country: 'China',
      registrationDate: '2023-12-05',
      status: 'pending',
      tier: 'Silver',
      totalReferrals: 45,
      activeReferrals: 23,
      totalCommission: 3450.0,
      currentMonthCommission: 450.0,
      lastActivity: '3 days ago',
      marketingMaterials: 6,
      conversionRate: 51.1,
    },
    {
      id: '4',
      partnerId: 'IB-004567',
      name: 'Emma Davis',
      email: 'emma.davis@ibpartner.com',
      phone: '+61 2 9876 5432',
      country: 'Australia',
      registrationDate: '2023-11-12',
      status: 'active',
      tier: 'Bronze',
      totalReferrals: 23,
      activeReferrals: 15,
      totalCommission: 1250.0,
      currentMonthCommission: 180.0,
      lastActivity: '5 hours ago',
      marketingMaterials: 4,
      conversionRate: 65.2,
    },
  ]);
  const [marketingMaterials] = useState<MarketingMaterial[]>([
    {
      id: '1',
      title: 'Forex Trading Banner - Summer Campaign',
      type: 'banner',
      category: 'Seasonal',
      downloads: 1250,
      usage: 890,
      createdAt: '2024-01-10',
      status: 'active',
    },
    {
      id: '2',
      title: 'Professional Trader Landing Page',
      type: 'landing-page',
      category: 'Conversion',
      downloads: 890,
      usage: 567,
      createdAt: '2024-01-08',
      status: 'active',
    },
    {
      id: '3',
      title: 'Welcome Email Template',
      type: 'email-template',
      category: 'Communication',
      downloads: 2100,
      usage: 1456,
      createdAt: '2024-01-05',
      status: 'active',
    },
    {
      id: '4',
      title: 'Social Media Posts - Q1 2024',
      type: 'social-media',
      category: 'Social',
      downloads: 670,
      usage: 234,
      createdAt: '2024-01-12',
      status: 'draft',
    },
  ]);

  const partnerFilterFields = [
    {
      id: 'status',
      label: 'Status',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'pending', label: 'Pending' },
        { value: 'suspended', label: 'Suspended' },
      ],
    },
    {
      id: 'tier',
      label: 'Tier',
      options: [
        { value: 'Bronze', label: 'Bronze' },
        { value: 'Silver', label: 'Silver' },
        { value: 'Gold', label: 'Gold' },
        { value: 'Platinum', label: 'Platinum' },
      ],
    },
    {
      id: 'country',
      label: 'Country',
      options: [
        { value: 'United States', label: 'United States' },
        { value: 'United Kingdom', label: 'United Kingdom' },
        { value: 'China', label: 'China' },
        { value: 'Australia', label: 'Australia' },
      ],
    },
  ];

  const materialFilterFields = [
    {
      id: 'type',
      label: 'Type',
      options: [
        { value: 'banner', label: 'Banner' },
        { value: 'landing-page', label: 'Landing Page' },
        { value: 'email-template', label: 'Email Template' },
        { value: 'social-media', label: 'Social Media' },
        { value: 'video', label: 'Video' },
      ],
    },
    {
      id: 'status',
      label: 'Status',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'draft', label: 'Draft' },
        { value: 'archived', label: 'Archived' },
      ],
    },
  ];

  const getMaterialTypeColor = (type: string) => {
    switch (type) {
      case 'banner':
        return 'bg-blue-100 text-blue-800';
      case 'landing-page':
        return 'bg-purple-100 text-purple-800';
      case 'email-template':
        return 'bg-green-100 text-green-800';
      case 'social-media':
        return 'bg-pink-100 text-pink-800';
      case 'video':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleFiltersApply = (filters: { [key: string]: string }) => {
    setActiveFilters(filters);
  };

  const handleFiltersClear = () => {
    setActiveFilters({});
  };

  const partnerColumns: DataTableColumn<IBPartner>[] = [
    {
      id: 'partnerId',
      header: 'Partner ID',
      accessor: row => (
        <span className="font-mono text-sm">{row.partnerId}</span>
      ),
      sortable: true,
      sortValue: row => row.partnerId,
      width: '120px',
    },
    {
      id: 'name',
      header: 'Name',
      accessor: row => (
        <div className="flex flex-col">
          <span className="font-medium">{row.name}</span>
          <span className="text-xs text-muted-foreground">{row.email}</span>
        </div>
      ),
      sortable: true,
      sortValue: row => row.name,
      width: '200px',
    },
    {
      id: 'tier',
      header: 'Tier',
      accessor: row => (
        <Badge className={getStatusColor(row.tier, 'tier')}>
          <Award className="h-3 w-3 mr-1" />
          {capitalizeFirst(row.tier)}
        </Badge>
      ),
      sortable: true,
      sortValue: row => row.tier,
      width: '100px',
    },
    {
      id: 'totalReferrals',
      header: 'Total Referrals',
      accessor: row => (
        <div className="text-center">
          <div className="font-semibold">{row.totalReferrals}</div>
          <div className="text-xs text-muted-foreground">
            {row.activeReferrals} active
          </div>
        </div>
      ),
      sortable: true,
      sortValue: row => row.totalReferrals,
      width: '120px',
    },
    {
      id: 'totalCommission',
      header: 'Total Commission',
      accessor: row => (
        <div className="text-right">
          <div className="font-semibold text-green-600">
            ${row.totalCommission.toLocaleString()}
          </div>
          <div className="text-xs text-muted-foreground">
            ${row.currentMonthCommission.toLocaleString()} this month
          </div>
        </div>
      ),
      sortable: true,
      sortValue: row => row.totalCommission,
      width: '150px',
    },
    {
      id: 'conversionRate',
      header: 'Conversion Rate',
      accessor: row => (
        <div className="flex items-center gap-2">
          <div className="text-right">
            <div className="font-semibold">{row.conversionRate}%</div>
          </div>
          <Target className="h-4 w-4 text-blue-500" />
        </div>
      ),
      sortable: true,
      sortValue: row => row.conversionRate,
      width: '120px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => (
        <Badge className={getStatusColor(row.status)}>
          {capitalizeFirst(row.status)}
        </Badge>
      ),
      sortable: true,
      sortValue: row => row.status,
      width: '100px',
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: row => (
        <ActionDropdown
          id={`partner-${row.id}`}
          size="icon"
          variant="ghost"
          showView={true}
          showEdit={true}
          showDelete={false}
          onView={() => console.log('View partner:', row.id)}
          onEdit={() => console.log('Edit partner:', row.id)}
        />
      ),
      width: '100px',
    },
  ];

  const materialColumns: DataTableColumn<MarketingMaterial>[] = [
    {
      id: 'title',
      header: 'Title',
      accessor: row => (
        <div className="flex flex-col">
          <span className="font-medium">{row.title}</span>
          <span className="text-xs text-muted-foreground">{row.category}</span>
        </div>
      ),
      sortable: true,
      sortValue: row => row.title,
      width: '250px',
    },
    {
      id: 'type',
      header: 'Type',
      accessor: row => (
        <Badge className={getMaterialTypeColor(row.type)}>
          {capitalizeFirst(row.type)}
        </Badge>
      ),
      sortable: true,
      sortValue: row => row.type,
      width: '120px',
    },
    {
      id: 'downloads',
      header: 'Downloads',
      accessor: row => (
        <div className="flex items-center gap-2">
          <Download className="h-4 w-4 text-blue-500" />
          <span className="font-semibold">
            {row.downloads.toLocaleString()}
          </span>
        </div>
      ),
      sortable: true,
      sortValue: row => row.downloads,
      width: '120px',
    },
    {
      id: 'usage',
      header: 'Usage',
      accessor: row => (
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-green-500" />
          <span className="font-semibold">{row.usage.toLocaleString()}</span>
        </div>
      ),
      sortable: true,
      sortValue: row => row.usage,
      width: '100px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => (
        <Badge className={getStatusColor(row.status)}>
          {capitalizeFirst(row.status)}
        </Badge>
      ),
      sortable: true,
      sortValue: row => row.status,
      width: '100px',
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: row => (
        <ActionDropdown
          id={`material-${row.id}`}
          size="icon"
          variant="ghost"
          showView={true}
          showEdit={false}
          showDelete={false}
          showDownload={true}
          onView={() => console.log('View material:', row.id)}
          onDownload={() => console.log('Download material:', row.id)}
        />
      ),
      width: '100px',
    },
  ];

  const stats = {
    totalPartners: partners.length,
    activePartners: partners.filter(p => p.status === 'active').length,
    totalCommission: partners.reduce((sum, p) => sum + p.totalCommission, 0),
    monthlyCommission: partners.reduce(
      (sum, p) => sum + p.currentMonthCommission,
      0
    ),
    totalReferrals: partners.reduce((sum, p) => sum + p.totalReferrals, 0),
    activeReferrals: partners.reduce((sum, p) => sum + p.activeReferrals, 0),
    totalMaterials: marketingMaterials.length,
    activeMaterials: marketingMaterials.filter(m => m.status === 'active')
      .length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title=" IB Marketing Management"
          description="Manage Introducing Broker partners and marketing materials"
        />

        {/* Tab Navigation */}
        <div className="pt-3 border-b border-border">
          <nav className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                activeTab === 'overview'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('partners')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                activeTab === 'partners'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              IB Partners
            </button>
            <button
              onClick={() => setActiveTab('materials')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                activeTab === 'materials'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Marketing Materials
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                activeTab === 'analytics'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Analytics
            </button>
          </nav>
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Total Partners
                    </p>
                    <p className="text-2xl font-bold">{stats.totalPartners}</p>
                    <p className="text-xs text-muted-foreground">
                      {stats.activePartners} active
                    </p>
                  </div>
                  <Users className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Total Commission
                    </p>
                    <p className="text-2xl font-bold text-green-600">
                      ${stats.totalCommission.toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      ${stats.monthlyCommission.toLocaleString()} this month
                    </p>
                  </div>
                  <DollarSign className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Total Referrals
                    </p>
                    <p className="text-2xl font-bold">{stats.totalReferrals}</p>
                    <p className="text-xs text-muted-foreground">
                      {stats.activeReferrals} active
                    </p>
                  </div>
                  <Target className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Marketing Materials
                    </p>
                    <p className="text-2xl font-bold">{stats.totalMaterials}</p>
                    <p className="text-xs text-muted-foreground">
                      {stats.activeMaterials} active
                    </p>
                  </div>
                  <Megaphone className="h-8 w-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button className="w-full justify-start" variant="outline">
                  <Users className="h-4 w-4 mr-2" />
                  Add New Partner
                </Button>
                <Button className="w-full justify-start" variant="outline">
                  <Gift className="h-4 w-4 mr-2" />
                  Create Marketing Material
                </Button>
                <Button className="w-full justify-start" variant="outline">
                  <Share2 className="h-4 w-4 mr-2" />
                  Send Newsletter
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Activity</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span>Sarah Johnson reached Platinum tier</span>
                  <span className="text-muted-foreground">2 hours ago</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>Summer campaign launched</span>
                  <span className="text-muted-foreground">5 hours ago</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>3 new partner applications</span>
                  <span className="text-muted-foreground">1 day ago</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Top Performers</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">Sarah Johnson</span>
                  <span className="text-green-600 font-semibold">$4,250</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">John Smith</span>
                  <span className="text-green-600 font-semibold">$2,340</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">Mike Chen</span>
                  <span className="text-green-600 font-semibold">$450</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Partners Tab */}
      {activeTab === 'partners' && (
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-4 w-full">
              <div>
                <CardTitle className="text-lg">IB Partners</CardTitle>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowInlineFilters(!showInlineFilters);
                  }}
                >
                  <Filter className="h-4 w-4 mr-2" />
                  {showInlineFilters ? 'Hide Filter' : 'Show Filter'}
                </Button>
                <Button variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <InlineFilter
              showFilters={showInlineFilters}
              onToggle={() => setShowInlineFilters(!showInlineFilters)}
              filterFields={partnerFilterFields}
              activeFilters={activeFilters}
              onFilterChange={setActiveFilters}
              onFiltersClear={handleFiltersClear}
              onFiltersApply={handleFiltersApply}
              title="Filter Partners"
            />
            <div className="mt-4">
              <DataTable
                columns={partnerColumns}
                data={partners}
                searchValue={searchValue}
                onSearchChange={setSearchValue}
                onSearchReset={() => setSearchValue('')}
                searchPlaceholder="Search partners by name, email, partner ID..."
                filterFields={partnerFilterFields}
                activeFilters={activeFilters}
                onFiltersChange={() => {}}
                initialPageSize={10}
                showColumnVisibility={true}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Marketing Materials Tab */}
      {activeTab === 'materials' && (
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-4 w-full">
              <div>
                <CardTitle className="text-lg">Marketing Materials</CardTitle>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowInlineFilters(!showInlineFilters);
                  }}
                >
                  <Filter className="h-4 w-4 mr-2" />
                  {showInlineFilters ? 'Hide Filter' : 'Show Filter'}
                </Button>
                <Button variant="outline" size="sm">
                  <Gift className="h-4 w-4 mr-2" />
                  Create Material
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <InlineFilter
              showFilters={showInlineFilters}
              onToggle={() => setShowInlineFilters(!showInlineFilters)}
              filterFields={materialFilterFields}
              activeFilters={activeFilters}
              onFilterChange={setActiveFilters}
              onFiltersClear={handleFiltersClear}
              onFiltersApply={handleFiltersApply}
              title="Filter Materials"
            />
            <div className="mt-4">
              <DataTable
                columns={materialColumns}
                data={marketingMaterials}
                searchValue={searchValue}
                onSearchChange={setSearchValue}
                onSearchReset={() => setSearchValue('')}
                searchPlaceholder="Search materials by title, category..."
                filterFields={materialFilterFields}
                activeFilters={activeFilters}
                onFiltersChange={() => {}}
                initialPageSize={10}
                showColumnVisibility={true}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Partner Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">
                      Top Tier
                    </span>
                    <span className="font-semibold">Platinum</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">
                      Avg. Commission
                    </span>
                    <span className="font-semibold text-green-600">$3,850</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">
                      Best Conversion
                    </span>
                    <span className="font-semibold">69.6%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">
                      Total Materials Used
                    </span>
                    <span className="font-semibold">3,147</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Material Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">
                      Most Downloaded
                    </span>
                    <span className="font-semibold">Email Templates</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">
                      Highest Usage
                    </span>
                    <span className="font-semibold">Banners</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">
                      Engagement Rate
                    </span>
                    <span className="font-semibold">67.3%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">
                      Active Campaigns
                    </span>
                    <span className="font-semibold">12</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Performance Chart Placeholder */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Commission Trends</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64 flex items-center justify-center text-muted-foreground border-2 border-dashed border-border rounded-lg">
                <div className="text-center">
                  <BarChart3 className="h-12 w-12 mx-auto mb-2" />
                  <p>Commission trends chart will be displayed here</p>
                  <p className="text-sm">
                    Integration with charting library needed
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default IBMarketing;
